import { useEffect, useState } from "react";
import {
  formatCoords,
  getCachedLocationName,
  getLocationCacheKey,
  getLocationCoords,
  reverseGeocodeLocation,
  toDisplayText,
} from "@/lib/geocode";

/**
 * Renders a resolved place name for a { lat, lon } location object, falling
 * back to the raw coordinates while resolving or on failure. Non-coordinate
 * values (plain strings, { address }, { place }, etc.) are rendered as-is.
 */
export default function LocationLabel({ locationValue, className = "", fallbackClassName = "" }) {
  const isCoordinateLocation = Boolean(getLocationCoords(locationValue));
  const [resolvedName, setResolvedName] = useState(() =>
    isCoordinateLocation ? getCachedLocationName(locationValue) || "" : "",
  );

  useEffect(() => {
    if (!isCoordinateLocation) {
      setResolvedName("");
      return undefined;
    }

    let cancelled = false;
    const cacheKey = getLocationCacheKey(locationValue);

    if (!cacheKey) {
      setResolvedName("");
      return undefined;
    }

    const cachedValue = getCachedLocationName(locationValue);
    if (cachedValue !== undefined) {
      setResolvedName(cachedValue || "");
      return undefined;
    }

    setResolvedName("");
    reverseGeocodeLocation(locationValue).then((placeName) => {
      if (!cancelled) {
        setResolvedName(placeName || "");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locationValue, isCoordinateLocation]);

  if (!isCoordinateLocation) {
    return <div className={className}>{toDisplayText(locationValue)}</div>;
  }

  const coordsText = formatCoords(locationValue);

  if (resolvedName) {
    return (
      <div className={className}>
        <div className="font-medium text-slate-900">{resolvedName}</div>
        <div className={`text-xs text-slate-500 ${fallbackClassName}`}>{coordsText}</div>
      </div>
    );
  }

  if (coordsText !== "-") {
    return (
      <div className={className}>
        <div className="text-slate-900">Resolving place…</div>
        <div className={`text-xs text-slate-500 ${fallbackClassName}`}>{coordsText}</div>
      </div>
    );
  }

  return <div className={className}>-</div>;
}
